/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hook for CMS Spec Versions reference data.
 *
 * Fetches the list of available CMS spec versions from ref.cms_spec_versions.
 * The default CMS spec version for ticket creation is the current entry of the
 * highest major family (the `is_current` row with the highest major_version).
 */

import { useQuery } from "@tanstack/react-query";
import { authenticatedApi, unwrapResponse } from "@/api/authenticatedClient";
import { canonicalCmsSpecVersion } from "@/lib/cmsSpecVersion";
import { useStateConfigDefaults } from "@/api/hooks/useStateConfig";
import type { ApiResponse } from "@/lib/types";

// ============================================================================
// Types
// ============================================================================

/**
 * One row from ref.cms_spec_versions as returned by the API.
 * API returns snake_case field names.
 */
export interface CmsSpecVersion {
  /** Full version code, e.g. "V4.1.0", "V4.0.0", "V3.1.13". Primary key. */
  cms_spec_version: string;
  /** Major version grouping, e.g. "V4", "V3". */
  major_version: string;
  /** Human-readable name, e.g. "T-MSIS V4.1.0". */
  name: string;
  /**
   * True when this is the current (highest-patch) version within its major family.
   * Exactly one row per major_version will have is_current = true.
   */
  is_current: boolean;
  /**
   * Processing module name, e.g. "v4" or "v3".
   * Matches the processing/{v3,v4}/ directory.
   */
  processing_module: string;
}

/** Backend response body: GET /rules/cms-spec-versions returns { versions: [...] }. */
interface CmsSpecVersionsResponse {
  versions: CmsSpecVersion[];
}

// ============================================================================
// Query Key
// ============================================================================

export const cmsSpecVersionsKeys = {
  all: ["cms-spec-versions"] as const,
} as const;

// ============================================================================
// API function
// ============================================================================

// Backend route: GET /api/v1/rules/cms-spec-versions (rules router).
// authenticatedApi prepends the /api/v1 base, so the path here is base-relative.
const CMS_SPEC_VERSIONS_ENDPOINT = "/rules/cms-spec-versions";

async function fetchCmsSpecVersions(): Promise<CmsSpecVersion[]> {
  // Backend returns ApiResponse<{ versions: CmsSpecVersion[] }>.
  const response = await authenticatedApi.get<ApiResponse<CmsSpecVersionsResponse>>(
    CMS_SPEC_VERSIONS_ENDPOINT
  );
  return unwrapResponse(response).versions;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Fetch the list of available CMS spec versions from ref.cms_spec_versions.
 *
 * Use this hook anywhere the user needs to select a CMS spec version (e.g.
 * ticket creation) or display a version badge with a human-readable name.
 *
 * The version catalog changes very rarely (only when a new CMS release is
 * seeded by ops), so it is cached aggressively.
 */
export function useCmsSpecVersions() {
  return useQuery<CmsSpecVersion[]>({
    queryKey: cmsSpecVersionsKeys.all,
    queryFn: fetchCmsSpecVersions,
    staleTime: 60 * 60 * 1000, // 1 hour — reference data, changes rarely
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

// ============================================================================
// Selector helpers
// ============================================================================

/**
 * From a list of CmsSpecVersion entries, return the default (current) version —
 * mirroring the backend resolver `WHERE is_current ORDER BY major_version DESC
 * LIMIT 1`: the `is_current` row of the **highest major family that has one**.
 *
 * Major-agnostic on purpose, so a newly adopted version (a new patch OR a new
 * major, e.g. a future V5) becomes the default with no code change. Deliberate
 * edge behavior, matching the backend: if a newer major is present but not yet
 * flagged `is_current` (a transient / partially-configured catalog), a
 * genuinely-current lower major is preferred over a non-current higher one. Only
 * when **no** version is current does it fall back to the highest-major entry; an
 * empty list returns undefined.
 */
export function resolveDefaultCmsSpecVersion(
  versions: CmsSpecVersion[]
): CmsSpecVersion | undefined {
  if (versions.length === 0) return undefined;
  // Numeric major rank so a future V10 outranks V4 (a lexical compare would put
  // "V4" above "V10"). Unparseable majors sort last.
  const majorRank = (v: CmsSpecVersion): number => {
    const n = Number.parseInt(v.major_version.replace(/^[Vv]/, ""), 10);
    return Number.isNaN(n) ? -1 : n;
  };
  const byMajorDesc = [...versions].sort((a, b) => majorRank(b) - majorRank(a));
  // Highest-major-first scan: the first is_current row is the current version of
  // the highest major that HAS one (see the docstring's edge note — a current
  // lower major is intentionally preferred over a non-current higher one). If no
  // version is current at all, fall back to the highest-major entry.
  return byMajorDesc.find((v) => v.is_current) ?? byMajorDesc[0];
}

/**
 * Resolve the default CMS spec version to preselect, honoring the version
 * chosen in State Configuration first so Analytics / Submissions / DQ Measures
 * all open scoped to the state's version (e.g. State Config V3.0.0 -> V3).
 *
 * `stateVersion` is the raw State-Config value — either bare ("3.0.0") or
 * prefixed ("V3.0.0"); it is canonicalized internally. Resolution order:
 *   1. Exact match of the State-Config version (e.g. "V3.0.0").
 *   2. Newest version sharing that major (any "V3.*"; prefer the is_current row)
 *      — so a State Config that names a patch we don't have still lands on V3.
 *   3. The generic catalog default (`resolveDefaultCmsSpecVersion`: the current
 *      version of the highest major family).
 *
 * Returns undefined only when `versions` is empty.
 */
export function resolveStateDefaultCmsSpecVersion(
  versions: CmsSpecVersion[],
  stateVersion?: string
): CmsSpecVersion | undefined {
  if (versions.length === 0) return undefined;

  const canonical = canonicalCmsSpecVersion(stateVersion);
  if (canonical) {
    const exact = versions.find((v) => v.cms_spec_version.toUpperCase() === canonical);
    if (exact) return exact;

    const major = canonical.split(".")[0]; // "V3.0.0" -> "V3"
    const sameMajor = versions.filter((v) => v.major_version.toUpperCase() === major);
    if (sameMajor.length > 0) {
      return sameMajor.find((v) => v.is_current) ?? sameMajor[0];
    }
  }

  return resolveDefaultCmsSpecVersion(versions);
}

/**
 * The state's adopted version as a **catalog row**, or undefined when it is not one.
 *
 * Deliberately not `resolveStateDefaultCmsSpecVersion`. That function's job is to pick a usable
 * *default*, so when the exact version is missing it falls through to the newest row of the same
 * major — which is right for "what should this open on" and wrong for "what is the cap". A State
 * Config reading `4.0` would resolve to V4.3.0 and cap Production at the newest release rather
 * than at nothing, which is the opposite of the intent.
 *
 * `config.state_configs.tmsis_version` is free text (`features/admin/schemas.py`), so a value can
 * be well-shaped and still not exist: `4.0` canonicalizes to `V4.0`, compares equal to `V4.0.0`,
 * and is not a version anyone can be given. `submissions.tickets.cms_spec_version` is a foreign
 * key, so handing one to the wizard produces a constraint violation at submit rather than a
 * validation message.
 *
 * This is the same test the backend applies in `_state_cms_spec_version_ceiling` — an adopted
 * version the catalog does not contain is treated as unset — reached the same way, so the two
 * sides cannot drift.
 */
export function resolveAdoptedCmsSpecVersion(
  versions: CmsSpecVersion[],
  stateVersion?: string
): string | undefined {
  const canonical = canonicalCmsSpecVersion(stateVersion);
  if (!canonical) return undefined;
  return versions.find((v) => v.cms_spec_version.toUpperCase() === canonical)?.cms_spec_version;
}

/**
 * The canonical CMS spec version configured in State Configuration, plus
 * whether that config query has settled.
 *
 * `version` is the canonicalized code ("V3.0.0") or undefined while loading /
 * when no state config exists. `isReady` is true once the query has resolved
 * (success or error) — callers gate their default-seeding on it so they don't
 * prematurely fall back to V4 before the state's real version arrives.
 */
export function useStateDefaultCmsSpecVersion(): {
  version: string | undefined;
  isReady: boolean;
} {
  const { data, isFetched, isFetching } = useStateConfigDefaults();
  return {
    version: canonicalCmsSpecVersion(data?.tmsis_version),
    // "Settled" = a fetch has completed and none is in flight. Deliberately NOT
    // `!isLoading`: that flips true on a transient/settled-empty state before the
    // real value arrives, which would let callers lock onto the fallback version
    // and never re-seed once the state config actually loads.
    isReady: isFetched && !isFetching,
  };
}
